import { type ReactNode, useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipAlign, TooltipPosition } from 'cpool-components/general/Tooltip/types';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant, type ButtonVariantType } from 'cpool-components/input/Button/types';
import { useTranslation } from 'react-i18next';
import { LuPen } from 'react-icons/lu';

import { cn } from '~/utils/cnTailwind';

export type ChangeTriggerModalProps = { onClose: () => void; open: boolean };

type Props = {
    disabled?: boolean;
    iconOnly?: boolean;
    label?: string;
    renderModal: (modal: ChangeTriggerModalProps) => ReactNode;
    testId: string;
    variant?: ButtonVariantType;
};

// pencil button that opens a change modal; the wrapper keeps clicks from reaching a clickable lead row
export const ChangeTriggerButton: React.FC<Props> = ({
    disabled = false,
    iconOnly = false,
    label: customLabel,
    renderModal,
    testId,
    variant = ButtonVariant.SECONDARY,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const label = customLabel ?? t('LEADS.ASSIGNED_USERS.CHANGE_TITLE');

    const button = (
        <Button
            ariaLabel={label}
            className={cn(!iconOnly && 'w-full')}
            data-testid={testId}
            disabled={disabled}
            iconLeft={<LuPen className="size-3.5" />}
            onClick={(evt) => {
                evt.preventDefault();
                setOpen(true);
            }}
            variant={variant}
        >
            {iconOnly ? undefined : label}
        </Button>
    );

    return (
        <div onClick={(evt) => evt.stopPropagation()}>
            {iconOnly ? (
                <Tooltip>
                    {/* the wrapper is the trigger so the label also shows while the button is disabled */}
                    <TooltipTrigger asChild>
                        <div>{button}</div>
                    </TooltipTrigger>
                    <TooltipContent align={TooltipAlign.END} side={TooltipPosition.TOP}>
                        {label}
                    </TooltipContent>
                </Tooltip>
            ) : (
                button
            )}
            {renderModal({ onClose: () => setOpen(false), open })}
        </div>
    );
};
